import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ModernCashRegister from '@/modern/staff/ModernCashRegister';
import { useIsModern } from '@/modern/shared';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Plus, 
  DollarSign, 
  Wallet,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Calculator,
  History,
  AlertCircle,
  Lock,
  User,
  Settings,
  Receipt
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

function CashRegisterPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [registers, setRegisters] = useState([]);
  const [selectedRegister, setSelectedRegister] = useState(null);
  const [movements, setMovements] = useState([]);
  const [history, setHistory] = useState([]);
  const [registerSessions, setRegisterSessions] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showOpenDialog, setShowOpenDialog] = useState(false);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [showMovementDialog, setShowMovementDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showSessionsDialog, setShowSessionsDialog] = useState(false);
  
  // Form states
  const [newRegisterName, setNewRegisterName] = useState('Caja Principal');
  const [newRegisterPin, setNewRegisterPin] = useState('');
  const [newRegisterPinRequired, setNewRegisterPinRequired] = useState(false);
  const [newRegisterAssignedUser, setNewRegisterAssignedUser] = useState('');
  const [openAmount, setOpenAmount] = useState('');
  const [openNotes, setOpenNotes] = useState('');
  const [openPin, setOpenPin] = useState('');
  const [closeAmount, setCloseAmount] = useState('');
  const [closeNotes, setCloseNotes] = useState('');
  const [closePin, setClosePin] = useState('');
  const [movementType, setMovementType] = useState('income');
  const [movementAmount, setMovementAmount] = useState('');
  const [movementDescription, setMovementDescription] = useState('');
  const [movementCategory, setMovementCategory] = useState('other');
  
  useEffect(() => {
    fetchRegisters();
    fetchHistory();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedRegister) {
      fetchMovements(selectedRegister.register_id);
    }
  }, [selectedRegister]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(response.data.filter(u => ['cashier', 'business_admin'].includes(u.role)));
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchRegisterSessions = async (registerId) => {
    try {
      const response = await axios.get(`${API}/cash-registers/${registerId}/sessions`, { withCredentials: true });
      setRegisterSessions(response.data);
      setShowSessionsDialog(true);
    } catch (error) {
      toast.error('Error al cargar sesiones');
    }
  };

  const fetchRegisters = async () => {
    try {
      const response = await axios.get(`${API}/cash-registers`, { withCredentials: true });
      setRegisters(response.data);
      if (response.data.length > 0 && !selectedRegister) {
        setSelectedRegister(response.data[0]);
      }
    } catch (error) {
      toast.error('Error al cargar cajas');
    } finally {
      setLoading(false);
    }
  };

  const fetchMovements = async (registerId) => {
    try {
      const response = await axios.get(`${API}/cash-registers/${registerId}/movements`, { withCredentials: true });
      setMovements(response.data);
    } catch (error) {
      console.error('Error fetching movements:', error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/cash-registers/history`, { withCredentials: true });
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleCreateRegister = async () => {
    try {
      const payload = { 
        name: newRegisterName,
        pin_required: newRegisterPinRequired,
        pin_code: newRegisterPinRequired ? newRegisterPin : null,
        assigned_user_id: newRegisterAssignedUser || null
      };
      await axios.post(`${API}/cash-registers`, payload, { withCredentials: true });
      toast.success('Caja creada');
      setShowCreateDialog(false);
      setNewRegisterName('Caja Principal');
      setNewRegisterPin('');
      setNewRegisterPinRequired(false);
      setNewRegisterAssignedUser('');
      fetchRegisters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear caja');
    }
  };

  const handleOpenRegister = async () => {
    if (!selectedRegister || !openAmount) return;
    try {
      const payload = { 
        initial_amount: parseFloat(openAmount), 
        notes: openNotes 
      };
      if (selectedRegister.pin_required) {
        payload.pin = openPin;
      }
      await axios.post(
        `${API}/cash-registers/${selectedRegister.register_id}/open`,
        payload,
        { withCredentials: true }
      );
      toast.success('Caja abierta');
      setShowOpenDialog(false);
      setOpenAmount('');
      setOpenNotes('');
      setOpenPin('');
      fetchRegisters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al abrir caja');
    }
  };

  const handleCloseRegister = async () => {
    if (!selectedRegister || !closeAmount) return;
    try {
      const payload = { 
        counted_amount: parseFloat(closeAmount), 
        notes: closeNotes 
      };
      if (selectedRegister.pin_required) {
        payload.pin = closePin;
      }
      const response = await axios.post(
        `${API}/cash-registers/${selectedRegister.register_id}/close`,
        payload,
        { withCredentials: true }
      );
      const diff = response.data.difference;
      const sessionsCount = response.data.sessions_paid_count || 0;
      if (diff === 0) {
        toast.success(`¡Caja cerrada! Cuadra perfecto. ${sessionsCount} mesas cobradas.`);
      } else if (diff > 0) {
        toast.success(`Caja cerrada. Sobrante: $${diff.toFixed(2)}. ${sessionsCount} mesas.`);
      } else {
        toast.warning(`Caja cerrada. Faltante: $${Math.abs(diff).toFixed(2)}. ${sessionsCount} mesas.`);
      }
      setShowCloseDialog(false);
      setCloseAmount('');
      setCloseNotes('');
      setClosePin('');
      fetchRegisters();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar caja');
    }
  };

  const handleAddMovement = async () => {
    if (!selectedRegister || !movementAmount || !movementDescription) return;
    try {
      await axios.post(
        `${API}/cash-registers/${selectedRegister.register_id}/movements`,
        {
          cash_register_id: selectedRegister.register_id,
          movement_type: movementType,
          amount: parseFloat(movementAmount),
          description: movementDescription,
          category: movementCategory
        },
        { withCredentials: true }
      );
      toast.success(movementType === 'income' ? 'Ingreso registrado' : 'Egreso registrado');
      setShowMovementDialog(false);
      setMovementAmount('');
      setMovementDescription('');
      setMovementCategory('other');
      fetchRegisters();
      fetchMovements(selectedRegister.register_id);
    } catch (error) {
      toast.error('Error al registrar movimiento');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-bold text-xl">Caja</h1>
              <p className="text-sm text-muted-foreground">Control de efectivo</p>
            </div>
          </div>
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nueva Caja
          </Button>
        </div>
      </header>

      <main className="p-4 max-w-6xl mx-auto">
        {registers.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Wallet className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No hay cajas configuradas</h3>
              <p className="text-muted-foreground mb-4">Crea tu primera caja para empezar</p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Crear Caja
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Register List */}
            <div className="space-y-4">
              <h2 className="font-semibold text-lg">Cajas</h2>
              {registers.map((register) => (
                <Card 
                  key={register.register_id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedRegister?.register_id === register.register_id 
                      ? 'ring-2 ring-primary' 
                      : ''
                  }`}
                  onClick={() => setSelectedRegister(register)}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{register.name}</h3>
                          {register.pin_required && (
                            <Lock className="w-3 h-3 text-amber-500" title="Requiere PIN" />
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge 
                            variant={register.status === 'open' ? 'default' : 'secondary'}
                            className={register.status === 'open' ? 'bg-green-700' : ''}
                          >
                            {register.status === 'open' ? 'Abierta' : 'Cerrada'}
                          </Badge>
                          {register.assigned_user_name && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {register.assigned_user_name}
                            </span>
                          )}
                        </div>
                      </div>
                      {register.status === 'open' && (
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-600">
                            ${register.current_amount?.toLocaleString()}
                          </p>
                          <p className="text-xs text-muted-foreground">Actual</p>
                        </div>
                      )}
                    </div>
                    {register.status === 'open' && register.opened_by_name && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Abierta por {register.opened_by_name}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Right: Selected Register Details */}
            {selectedRegister && (
              <div className="lg:col-span-2 space-y-4">
                <Card>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle>{selectedRegister.name}</CardTitle>
                        <CardDescription>
                          {selectedRegister.status === 'open' 
                            ? `Abierta desde ${new Date(selectedRegister.opened_at).toLocaleString()}`
                            : 'Cerrada'}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        {selectedRegister.status === 'closed' ? (
                          <Button onClick={() => setShowOpenDialog(true)}>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Abrir Caja
                          </Button>
                        ) : (
                          <>
                            <Button variant="outline" onClick={() => setShowMovementDialog(true)}>
                              <DollarSign className="w-4 h-4 mr-2" />
                              Movimiento
                            </Button>
                            <Button variant="destructive" onClick={() => setShowCloseDialog(true)}>
                              <XCircle className="w-4 h-4 mr-2" />
                              Cerrar Caja
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  {selectedRegister.status === 'open' && (
                    <CardContent>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="text-center p-4 bg-blue-50 dark:bg-blue-950 rounded-xl">
                          <p className="text-sm text-muted-foreground">Apertura</p>
                          <p className="text-xl font-bold text-blue-600">
                            ${selectedRegister.initial_amount?.toLocaleString()}
                          </p>
                        </div>
                        <div className="text-center p-4 bg-green-50 dark:bg-green-950 rounded-xl">
                          <p className="text-sm text-muted-foreground">Actual</p>
                          <p className="text-xl font-bold text-green-600">
                            ${selectedRegister.current_amount?.toLocaleString()}
                          </p>
                        </div>
                        <div className="text-center p-4 bg-orange-50 dark:bg-orange-950 rounded-xl">
                          <p className="text-sm text-muted-foreground">Esperado</p>
                          <p className="text-xl font-bold text-orange-600">
                            ${selectedRegister.expected_amount?.toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  )}
                </Card>

                {/* Tabs for Movements and History */}
                <Tabs defaultValue="movements">
                  <TabsList className="w-full">
                    <TabsTrigger value="movements" className="flex-1">
                      <DollarSign className="w-4 h-4 mr-2" />
                      Movimientos
                    </TabsTrigger>
                    <TabsTrigger value="history" className="flex-1">
                      <History className="w-4 h-4 mr-2" />
                      Historial de Cierres
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="movements">
                    <Card>
                      <CardContent className="p-0">
                        <ScrollArea className="h-[400px]">
                          {movements.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                              <Calculator className="w-12 h-12 mx-auto mb-2 opacity-50" />
                              <p>No hay movimientos</p>
                            </div>
                          ) : (
                            <div className="divide-y">
                              {movements.map((mov) => (
                                <div key={mov.movement_id} className="p-4 flex justify-between items-center">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                      mov.movement_type === 'income' 
                                        ? 'bg-green-100 text-green-600' 
                                        : 'bg-red-100 text-red-600'
                                    }`}>
                                      {mov.movement_type === 'income' 
                                        ? <TrendingUp className="w-5 h-5" />
                                        : <TrendingDown className="w-5 h-5" />
                                      }
                                    </div>
                                    <div>
                                      <p className="font-medium">{mov.description}</p>
                                      <p className="text-sm text-muted-foreground">
                                        {mov.category} • {mov.created_by_name}
                                      </p>
                                    </div>
                                  </div>
                                  <div className={`text-lg font-bold ${
                                    mov.movement_type === 'income' ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {mov.movement_type === 'income' ? '+' : '-'}${mov.amount?.toLocaleString()}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="history">
                    <Card>
                      <CardContent className="p-0">
                        <ScrollArea className="h-[400px]">
                          {history.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground">
                              <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                              <p>No hay cierres registrados</p>
                            </div>
                          ) : (
                            <div className="divide-y">
                              {history.map((h) => (
                                <div key={h.history_id} className="p-4">
                                  <div className="flex justify-between items-start mb-2">
                                    <div>
                                      <p className="font-medium">
                                        {new Date(h.closed_at).toLocaleDateString()}
                                      </p>
                                      <p className="text-sm text-muted-foreground">
                                        Cerrado por {h.closed_by_name}
                                      </p>
                                    </div>
                                    <Badge className={
                                      h.difference === 0 
                                        ? 'bg-green-500' 
                                        : h.difference > 0 
                                          ? 'bg-blue-500' 
                                          : 'bg-red-500'
                                    }>
                                      {h.difference === 0 
                                        ? 'Cuadra' 
                                        : h.difference > 0 
                                          ? `+$${h.difference.toFixed(2)}` 
                                          : `-$${Math.abs(h.difference).toFixed(2)}`
                                      }
                                    </Badge>
                                  </div>
                                  <div className="grid grid-cols-3 gap-2 text-sm">
                                    <div>
                                      <span className="text-muted-foreground">Apertura:</span>
                                      <span className="ml-1 font-medium">${h.initial_amount}</span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">Esperado:</span>
                                      <span className="ml-1 font-medium">${h.expected_amount}</span>
                                    </div>
                                    <div>
                                      <span className="text-muted-foreground">Contado:</span>
                                      <span className="ml-1 font-medium">${h.counted_amount}</span>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nueva Caja</DialogTitle>
            <DialogDescription>Crea una nueva caja registradora</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre de la caja</Label>
              <Input
                value={newRegisterName}
                onChange={(e) => setNewRegisterName(e.target.value)}
                placeholder="Ej: Caja Principal, Caja Barra"
              />
            </div>
            <div className="space-y-2">
              <Label>Responsable asignado (opcional)</Label>
              <Select value={newRegisterAssignedUser} onValueChange={setNewRegisterAssignedUser}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar usuario" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Sin asignar</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.user_id} value={user.user_id}>
                      {user.name} ({user.role})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Solo este usuario podrá operar la caja</p>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="pin-required"
                checked={newRegisterPinRequired}
                onChange={(e) => setNewRegisterPinRequired(e.target.checked)}
                className="rounded border-gray-300"
              />
              <Label htmlFor="pin-required">Requerir PIN para abrir/cerrar</Label>
            </div>
            {newRegisterPinRequired && (
              <div className="space-y-2">
                <Label>PIN (4-6 dígitos)</Label>
                <Input
                  type="password"
                  maxLength={6}
                  value={newRegisterPin}
                  onChange={(e) => setNewRegisterPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="****"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancelar</Button>
            <Button 
              onClick={handleCreateRegister}
              disabled={newRegisterPinRequired && (!newRegisterPin || newRegisterPin.length < 4)}
            >
              Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Open Dialog */}
      <Dialog open={showOpenDialog} onOpenChange={setShowOpenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Abrir Caja</DialogTitle>
            <DialogDescription>
              {selectedRegister?.assigned_user_name 
                ? `Asignada a: ${selectedRegister.assigned_user_name}` 
                : 'Ingresa el monto inicial para abrir la caja'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Monto Inicial</Label>
              <Input
                type="number"
                value={openAmount}
                onChange={(e) => setOpenAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
            {selectedRegister?.pin_required && (
              <div className="space-y-2">
                <Label>PIN de la caja</Label>
                <Input
                  type="password"
                  maxLength={6}
                  value={openPin}
                  onChange={(e) => setOpenPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="****"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Textarea
                value={openNotes}
                onChange={(e) => setOpenNotes(e.target.value)}
                placeholder="Observaciones..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOpenDialog(false)}>Cancelar</Button>
            <Button 
              onClick={handleOpenRegister}
              disabled={!openAmount || (selectedRegister?.pin_required && !openPin)}
            >
              Abrir Caja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close Dialog */}
      <Dialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cerrar Caja</DialogTitle>
            <DialogDescription>
              Monto esperado: <strong>${selectedRegister?.expected_amount?.toLocaleString()}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Monto Contado</Label>
              <Input
                type="number"
                value={closeAmount}
                onChange={(e) => setCloseAmount(e.target.value)}
                placeholder="0.00"
              />
              {closeAmount && selectedRegister && (
                <div className={`text-sm p-2 rounded ${
                  parseFloat(closeAmount) === selectedRegister.expected_amount 
                    ? 'bg-green-100 text-green-700' 
                    : parseFloat(closeAmount) > selectedRegister.expected_amount 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-red-100 text-red-700'
                }`}>
                  {parseFloat(closeAmount) === selectedRegister.expected_amount 
                    ? '✓ La caja cuadra perfectamente' 
                    : parseFloat(closeAmount) > selectedRegister.expected_amount 
                      ? `Sobrante: $${(parseFloat(closeAmount) - selectedRegister.expected_amount).toFixed(2)}` 
                      : `Faltante: $${(selectedRegister.expected_amount - parseFloat(closeAmount)).toFixed(2)}`
                  }
                </div>
              )}
            </div>
            {selectedRegister?.pin_required && (
              <div className="space-y-2">
                <Label>PIN de la caja</Label>
                <Input
                  type="password"
                  maxLength={6}
                  value={closePin}
                  onChange={(e) => setClosePin(e.target.value.replace(/\D/g, ''))}
                  placeholder="****"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Notas (opcional)</Label>
              <Textarea
                value={closeNotes}
                onChange={(e) => setCloseNotes(e.target.value)}
                placeholder="Observaciones del cierre..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCloseDialog(false)}>Cancelar</Button>
            <Button 
              variant="destructive" 
              onClick={handleCloseRegister}
              disabled={!closeAmount || (selectedRegister?.pin_required && !closePin)}
            >
              Cerrar Caja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement Dialog */}
      <Dialog open={showMovementDialog} onOpenChange={setShowMovementDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Movimiento</DialogTitle>
            <DialogDescription>Ingreso o egreso de efectivo</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <div className="flex gap-2">
                <Button
                  variant={movementType === 'income' ? 'default' : 'outline'}
                  className={movementType === 'income' ? 'bg-green-500 hover:bg-green-600' : ''}
                  onClick={() => setMovementType('income')}
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Ingreso
                </Button>
                <Button
                  variant={movementType === 'expense' ? 'default' : 'outline'}
                  className={movementType === 'expense' ? 'bg-red-500 hover:bg-red-600' : ''}
                  onClick={() => setMovementType('expense')}
                >
                  <TrendingDown className="w-4 h-4 mr-2" />
                  Egreso
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Monto</Label>
              <Input
                type="number"
                value={movementAmount}
                onChange={(e) => setMovementAmount(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label>Categoría</Label>
              <Select value={movementCategory} onValueChange={setMovementCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sale">Venta</SelectItem>
                  <SelectItem value="tip">Propina</SelectItem>
                  <SelectItem value="supplier">Proveedor</SelectItem>
                  <SelectItem value="refund">Devolución</SelectItem>
                  <SelectItem value="other">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Input
                value={movementDescription}
                onChange={(e) => setMovementDescription(e.target.value)}
                placeholder="Detalle del movimiento"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMovementDialog(false)}>Cancelar</Button>
            <Button onClick={handleAddMovement}>Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CashRegisterPagePage() {
  return useIsModern() ? <ModernCashRegister /> : <CashRegisterPage />;
}
